import React, { useEffect, useRef } from 'react'

import { useBuyBonusStore, useConfigurationStore, useFreeBuyBonusStore } from '@stores'

import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { formatMoney } from '@utils/money'
import { useShrinkTextToFit } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'

const FBBEndBonus: React.FC = () => {
    const totalWin = useFreeBuyBonusStore((state) => state.totalWin)
    const setTotalWin = useFreeBuyBonusStore((state) => state.setTotalWin)
    const bonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const styles = useFreeBuyBonusStore((state) => state.styles)
    const hasAwardedFreeBuyBonuses = useFreeBuyBonusStore((state) => state.hasAwardedFreeBuyBonuses)
    const hasActiveBonusesLeft = useFreeBuyBonusStore((state) => state.hasActiveBonusesLeft)

    const { t } = useTranslation()
    const currency = useConfigurationStore((state) => state.currency)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)

    const formattedTotalWin = formatMoney(+totalWin, currencyHideCents)
    const totalWinAmountRef = useRef<HTMLParagraphElement>(null)
    const isZeroWin = totalWin === '0'

    const text =
        hasActiveBonusesLeft || hasAwardedFreeBuyBonuses
            ? t('gameui.bonusSystem.stillHaveActiveBonuses')
            : t('gameui.bonusSystem.playingWithRealMoney')

    const image =
        bonuses[0] &&
        bonuses[0].typeConfiguration!.freeBuyBonusConfiguration?.imageName &&
        findElementByImageName(bonuses[0].typeConfiguration!.freeBuyBonusConfiguration!.imageName)

    const imageStyles = {
        ...(image ? buildAtlasImageStyles(image.atlasName, image.value, cachedImages[image.atlasName]) : {}),
        height: image ? `${image.value.height}px` : undefined,
    }

    useEffect(() => {
        return () => setTotalWin('0')
    }, [])

    useShrinkTextToFit({
        refresh: [formattedTotalWin],
        designFontSize: 128,
        bounds: { width: 590 },
        textRef: totalWinAmountRef,
    })

    const renderZeroWinContent = () => {
        return (
            <>
                <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold pt-10">
                    {t('gameui.bonusSystem.youDidNotScoreAWin') ?? "You didn't score a win"}
                </p>

                <p style={{ color: styles.primaryTextColor }} className="text-4xl font-black leading-tight pt-2">
                    {t('gameui.bonusSystem.betterLuckNextTime') ?? 'BETTER LUCK NEXT TIME!'}
                </p>

                <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold pt-14">
                    {text}
                </p>
            </>
        )
    }

    const renderWinContent = () => {
        return (
            <>
                <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold">
                    {t('gameui.bonusSystem.thisBonusHasEnded')}
                </p>

                <p style={{ color: styles.primaryTextColor }} className="text-4xl font-black leading-tight">
                    {t('gameui.bonusSystem.youHaveWon')}
                </p>

                <div style={{ color: styles.primaryTextColor }} className="flex flex-col items-center leading-tight">
                    <p className="text-9xl font-black" ref={totalWinAmountRef}>
                        {formattedTotalWin}
                        <span className="text-5xl font-black pl-2">{currency}</span>
                    </p>
                </div>

                <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold">
                    {text}
                </p>
            </>
        )
    }

    return (
        <div className="flex flex-col items-center justify-between">
            <div className="flex flex-col w-full">
                <div style={imageStyles} className="m-[1px]" /> {/* adding 1px to compensate for image misalignment */}
                <div
                    id="end-bonus-text"
                    style={{ ...styles.bodyBackground }}
                    className="flex flex-col items-center w-full h-[273px] mt-[-2px]"
                >
                    {isZeroWin ? renderZeroWinContent() : renderWinContent()}
                </div>
            </div>
        </div>
    )
}

export default FBBEndBonus
